/**
 * useZoneClassifier — classifies pipeline steps into layout zones.
 *
 * Combines visualizer detection, preference-based filtering,
 * zone classification, and step numbering into a single hook.
 *
 * Returns:
 *   zones              — headVizs / transforms / tailVizs
 *   transformStepNumbers — numbering for non-visualizer transform steps
 */

import { useMemo } from 'react';

import { useVisualizerStepDetection } from '@/hooks/useVisualizerStepDetection';
import type { IPipelineStep } from '@/interfaces/IPipeline';
import { classifyPipelineZones, type PipelineZones } from '@/lib/classifyPipelineZones';
import { usePreferencesStore } from '@/store/usePreferencesStore';

export interface ZoneClassification {
  zones: PipelineZones;
  transformStepNumbers: (number | undefined)[];
}

export function useZoneClassifier(pipeline: IPipelineStep[]): ZoneClassification {
  const showVisualizersInPipeline = usePreferencesStore((s) => s.showVisualizersInPipeline);
  const { hints } = useVisualizerStepDetection(pipeline);

  const filteredHints = useMemo(
    () => showVisualizersInPipeline ? hints : hints.filter((h) => !h.isVisualizer),
    [hints, showVisualizersInPipeline]
  );

  const zones = useMemo(() => classifyPipelineZones(filteredHints), [filteredHints]);

  const transformStepNumbers = useMemo(() => {
    let count = 0;
    return zones.transforms.map((h) => (h.isVisualizer ? undefined : ++count));
  }, [zones.transforms]);

  return { zones, transformStepNumbers };
}
